import { Plus, X } from "lucide-react-native";
import { FC, useCallback, useMemo, useState } from "react";

import {
  Card,
  ErrorWithRetry,
  HStack,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import API from "@/hooks/api";
import {
  CountryValue,
  DemographicDistribution,
  DemographicOption,
  DemographicSetting,
} from "@/types/resources";

import AgeRangeEditor from "./AgeRangeEditor";
import BehaviorEditor from "./BehaviorEditor";
import { default as CountryEditor } from "./countryEditor";
import DemographicDistributionEditor from "./DemographicDistributionEditor";
import DemographicSettingEditor from "./DemographicSettingEditor";
import DemographicSettings from "./DemographicSettings";

/* ── Props ───────────────────────────────────────── */

interface DemographicsEditorProps {
  demographics: DemographicSetting<Exclude<DemographicOption, "kids_age">>[];
  disabled?: boolean;
  onChange: (
    demographics: DemographicSetting<Exclude<DemographicOption, "kids_age">>[]
  ) => void;
  readonly?: boolean;
}

/* ── Component ───────────────────────────────────── */

const DemographicsEditor: FC<DemographicsEditorProps> = ({
  demographics,
  disabled = false,
  onChange,
  readonly = false,
}) => {
  const { i18n } = useLocalization();

  const {
    demographics: demographicsOptions,
    error: demographicsError,
    loading: isDemographicsLoading,
    refresh: refreshDemographics,
  } = API.Metadata.useGetDemographics();

  /* ── Derived demographic slices ── */

  const availableCountries = useMemo(() => {
    const countryDemo = demographicsOptions.find((d) => d.value === "country");
    if (!countryDemo) return [];
    return countryDemo.options.map((o) => ({
      label: o.label,
      value: o.value as CountryValue,
    }));
  }, [demographicsOptions]);

  const countryDistribution = useMemo(
    () =>
      (demographics.find((d) => d.demographic === "country")?.distribution ??
        []) as DemographicDistribution<"country">[],
    [demographics]
  );

  const showRaceEthnicityDemographic = useMemo(() => {
    const codes = countryDistribution.map((d) => d.value);
    return codes.includes("us") || codes.includes("ca");
  }, [countryDistribution]);

  const ageDistribution = useMemo(() => {
    const ageSetting = demographics.find((d) => d.demographic === "age");
    return (ageSetting?.distribution ?? [
      { label: "25-45", value: { max: 45, min: 25 }, weight: 1 },
    ]) as DemographicDistribution<"age">[];
  }, [demographics]);

  const genderOptions = useMemo(
    () => demographicsOptions.find((d) => d.value === "gender")?.options ?? [],
    [demographicsOptions]
  );

  const genderDistribution = useMemo(
    () =>
      demographics.find((d) => d.demographic === "gender")?.distribution ?? [],
    [demographics]
  );

  /* ── Update helpers ── */

  const replaceSlice = useCallback(
    (
      demographic: DemographicOption,
      distribution: DemographicDistribution[],
      label: string
    ) => {
      const idx = demographics.findIndex((d) => d.demographic === demographic);
      if (distribution.length === 0) {
        onChange(demographics.filter((d) => d.demographic !== demographic));
      } else if (idx === -1) {
        onChange([
          ...demographics,
          { demographic, distribution, label },
        ] as DemographicSetting<Exclude<DemographicOption, "kids_age">>[]);
      } else {
        const next = [...demographics] as DemographicSetting<
          Exclude<DemographicOption, "kids_age">
        >[];
        next[idx] = { demographic, distribution, label } as DemographicSetting<
          Exclude<DemographicOption, "kids_age">
        >;
        onChange(next);
      }
    },
    [demographics, onChange]
  );

  const handleCountryEditorChange = useCallback(
    (countries: DemographicDistribution<"country">[]) => {
      const countryLabel =
        demographicsOptions.find((d) => d.value === "country")?.label ?? "";
      const hasUsOrCa = countries.some(
        (c) => c.value === "us" || c.value === "ca"
      );
      const next = demographics.filter(
        (d) =>
          d.demographic !== "country" && (hasUsOrCa || d.demographic !== "race")
      ) as DemographicSetting<Exclude<DemographicOption, "kids_age">>[];
      const anchorIdx = demographics.findIndex(
        (d) => d.demographic === "country"
      );
      if (countries.length > 0) {
        const entry: DemographicSetting<"country"> = {
          demographic: "country" as const,
          distribution: countries,
          label: countryLabel,
        };
        if (anchorIdx === -1) {
          next.push(entry);
        } else {
          next.splice(anchorIdx, 0, entry);
        }
      }
      onChange(next);
    },
    [demographics, demographicsOptions, onChange]
  );

  const handleAgeChange = useCallback(
    (dist: DemographicDistribution<"age">[]) => {
      const ageLabel =
        demographicsOptions.find((d) => d.value === "age")?.label ??
        i18n.t("audienceBuilder.ageRange") ??
        "";
      replaceSlice("age", dist as DemographicDistribution[], ageLabel);
    },
    [demographicsOptions, i18n, replaceSlice]
  );

  const handleGenderChange = useCallback(
    (dist: DemographicDistribution[]) => {
      const genderLabel =
        demographicsOptions.find((d) => d.value === "gender")?.label ??
        i18n.t("audienceBuilder.gender") ??
        "";
      replaceSlice("gender", dist, genderLabel);
    },
    [demographicsOptions, i18n, replaceSlice]
  );

  const getDistribution = useCallback(
    (demographic: DemographicOption) =>
      demographics.find((d) => d.demographic === demographic)?.distribution ??
      [],
    [demographics]
  );

  const behaviorGroups = useMemo(
    () =>
      demographics
        .filter((d) => d.demographic === "behavior")
        .map((d) => d.distribution as DemographicDistribution<"behavior">[]),
    [demographics]
  );

  const [pendingGroupCount, setPendingGroupCount] = useState(0);

  const baseGroupsLength =
    behaviorGroups.length === 0 ? 1 : behaviorGroups.length;

  const totalDisplayGroups = useMemo(
    () => [
      ...(behaviorGroups.length === 0
        ? ([[]] as DemographicDistribution<"behavior">[][])
        : behaviorGroups),
      ...Array.from(
        { length: pendingGroupCount },
        () => [] as DemographicDistribution<"behavior">[]
      ),
    ],
    [behaviorGroups, pendingGroupCount]
  );

  const handleBehaviorGroupChange = useCallback(
    (groupIdx: number, dist: DemographicDistribution<"behavior">[]) => {
      const label = i18n.t("dashboardNavigator.behaviorLabel");
      const withoutBehaviors = demographics.filter(
        (d) => d.demographic !== "behavior"
      );

      if (groupIdx >= baseGroupsLength) {
        if (dist.length > 0) {
          setPendingGroupCount((c) => c - 1);
          onChange([
            ...withoutBehaviors,
            ...behaviorGroups.map((g) => ({
              demographic: "behavior" as const,
              distribution: g,
              label,
            })),
            { demographic: "behavior" as const, distribution: dist, label },
          ] as DemographicSetting<Exclude<DemographicOption, "kids_age">>[]);
        }
        return;
      }

      const effectiveGroups =
        behaviorGroups.length === 0
          ? ([[]] as DemographicDistribution<"behavior">[][])
          : behaviorGroups;
      const updatedGroups = effectiveGroups
        .map((g, i) => (i === groupIdx ? dist : g))
        .filter((g) => g.length > 0);
      onChange([
        ...withoutBehaviors,
        ...updatedGroups.map((g) => ({
          demographic: "behavior" as const,
          distribution: g,
          label,
        })),
      ] as DemographicSetting<Exclude<DemographicOption, "kids_age">>[]);
    },
    [baseGroupsLength, behaviorGroups, demographics, i18n, onChange]
  );

  const handleAddBehaviorGroup = useCallback(() => {
    setPendingGroupCount((c) => c + 1);
  }, []);

  const handleRemoveBehaviorGroup = useCallback(
    (groupIdx: number) => {
      if (groupIdx >= baseGroupsLength) {
        setPendingGroupCount((c) => c - 1);
        return;
      }
      const label = i18n.t("dashboardNavigator.behaviorLabel");
      const withoutBehaviors = demographics.filter(
        (d) => d.demographic !== "behavior"
      );
      const updatedGroups = behaviorGroups.filter((_, i) => i !== groupIdx);
      onChange([
        ...withoutBehaviors,
        ...updatedGroups.map((g) => ({
          demographic: "behavior" as const,
          distribution: g,
          label,
        })),
      ] as DemographicSetting<Exclude<DemographicOption, "kids_age">>[]);
    },
    [baseGroupsLength, behaviorGroups, demographics, i18n, onChange]
  );

  /* ── Render ── */

  /* Age + Gender share a row (each half) and every other demographic stacks
     full-width beneath them. Built as explicit rows rather than a wrapping
     flex grid so the layout is deterministic across web/native. */
  const editableDemographics = demographicsOptions.filter(
    (d) =>
      d.value !== "country" &&
      (showRaceEthnicityDemographic || d.value !== "race")
  );
  const ageOption = editableDemographics.find((d) => d.value === "age");
  const genderOption = editableDemographics.find((d) => d.value === "gender");
  const otherDemographics = editableDemographics.filter(
    (d) => d.value !== "age" && d.value !== "gender"
  );

  return (
    <VStack className="flex-[2] gap-10">
      {/* Country map */}
      {demographicsError ? (
        <ErrorWithRetry
          error={demographicsError}
          onRetry={refreshDemographics}
          title={i18n.t("dashboardNavigator.errorLoadingDemographics")}
        />
      ) : isDemographicsLoading ? (
        <VStack className="gap-3">
          <HStack className="items-center gap-2">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-4 w-32 rounded" />
          </HStack>
          <Skeleton className="h-[420px] w-full rounded-2xl" />
        </VStack>
      ) : (
        <CountryEditor
          countryDistribution={countryDistribution}
          disabled={disabled}
          items={availableCountries}
          onChange={handleCountryEditorChange}
          readonly={readonly}
        />
      )}

      {/* Demographics */}
      {!demographicsError && (
        <VStack className="gap-4">
          {(!readonly ||
            demographics.some(
              (d) =>
                d.demographic !== "country" &&
                (showRaceEthnicityDemographic || d.demographic !== "race")
            )) && (
            <Text className="text-sm font-semibold text-typography-900">
              {readonly
                ? i18n.t("audienceBuilder.demographicsReadonly")
                : i18n.t("audienceBuilder.demographics")}
            </Text>
          )}
          {isDemographicsLoading ? (
            <VStack className="gap-3">
              {[1, 2, 3, 4, 5].map((sk) => (
                <Card
                  className="border border-outline-200 rounded-2xl p-4 w-full"
                  key={sk}
                >
                  <Skeleton className="h-4 w-24 rounded mb-3" />
                  <HStack className="flex-wrap gap-2">
                    {[1, 2, 3, 4].map((j) => (
                      <Skeleton className="h-7 w-20 rounded-full" key={j} />
                    ))}
                  </HStack>
                </Card>
              ))}
            </VStack>
          ) : readonly ? (
            <DemographicSettings
              demographics={demographics.filter(
                (d) =>
                  d.demographic !== "country" &&
                  (showRaceEthnicityDemographic || d.demographic !== "race")
              )}
            />
          ) : (
            <VStack className="gap-6">
              {/* Age + Gender: side by side on desktop, stacked on mobile */}
              <HStack className="flex-col lg:flex-row gap-4 items-stretch">
                {ageOption && (
                  <Card
                    className="w-full lg:flex-1 border border-outline-200 rounded-2xl p-4 justify-between"
                    key="age"
                  >
                    <Text className="text-xs font-medium text-typography-500">
                      {i18n.t("audienceBuilder.ageRange")}
                    </Text>
                    <AgeRangeEditor
                      distribution={ageDistribution}
                      onChange={handleAgeChange}
                    />
                  </Card>
                )}

                {genderOption && (
                  <Card
                    className="w-full lg:flex-1 border border-outline-200 rounded-2xl p-4"
                    key="gender"
                  >
                    <HStack className="items-center justify-between mb-3">
                      <Text className="text-xs font-medium text-typography-500">
                        {genderOption.label}
                      </Text>
                      <DemographicDistributionEditor
                        distribution={genderDistribution}
                        onChange={handleGenderChange}
                        title={genderOption.label}
                        totalOptions={genderOptions.length}
                      />
                    </HStack>
                    <DemographicSettingEditor
                      availableOptions={genderOptions}
                      distribution={genderDistribution}
                      onChange={handleGenderChange}
                      title={genderOption.label}
                    />
                  </Card>
                )}
              </HStack>

              {/* Every other demographic stacks full-width below */}
              {otherDemographics.map((demo) => (
                <Card
                  className="w-full border border-outline-200 rounded-2xl p-4"
                  key={demo.value}
                >
                  <HStack className="items-center justify-between mb-3">
                    <Text className="text-xs font-medium text-typography-500">
                      {demo.label}
                    </Text>
                    <DemographicDistributionEditor
                      distribution={getDistribution(demo.value)}
                      onChange={(dist) =>
                        replaceSlice(demo.value, dist, demo.label)
                      }
                      title={demo.label}
                      totalOptions={demo.options.length}
                    />
                  </HStack>
                  <DemographicSettingEditor
                    availableOptions={demo.options}
                    distribution={getDistribution(demo.value)}
                    onChange={(dist) =>
                      replaceSlice(demo.value, dist, demo.label)
                    }
                    title={demo.label}
                  />
                </Card>
              ))}

              {/* Behavior section */}
              <VStack className="gap-3">
                <Text className="text-sm font-semibold text-typography-900">
                  {i18n.t("audienceBuilder.behaviors")}
                </Text>
                <VStack className="gap-4">
                  {totalDisplayGroups.map((group, idx) => (
                    <Card
                      className="border border-outline-200 rounded-2xl p-4"
                      key={idx}
                    >
                      <HStack className="items-center justify-between mb-3">
                        <Text className="text-xs font-medium text-typography-500">
                          {i18n.t("audienceBuilder.behaviorGroup", {
                            index: idx + 1,
                          })}
                        </Text>
                        <HStack className="items-center gap-2">
                          <DemographicDistributionEditor
                            disabled={disabled}
                            distribution={group}
                            onChange={(dist) =>
                              handleBehaviorGroupChange(
                                idx,
                                dist as DemographicDistribution<"behavior">[]
                              )
                            }
                            title={i18n.t("audienceBuilder.behaviorGroup", {
                              index: idx + 1,
                            })}
                          />
                          {!disabled && totalDisplayGroups.length >= 2 && (
                            <Pressable
                              onPress={() => handleRemoveBehaviorGroup(idx)}
                            >
                              <X
                                color="rgb(var(--color-typography-400))"
                                size={14}
                              />
                            </Pressable>
                          )}
                        </HStack>
                      </HStack>
                      <BehaviorEditor
                        distribution={group}
                        onChange={(dist) =>
                          handleBehaviorGroupChange(idx, dist)
                        }
                      />
                    </Card>
                  ))}
                  {totalDisplayGroups.length < 5 && (
                    <Pressable
                      className="border border-outline-200 rounded-xl p-3 items-center"
                      disabled={disabled}
                      onPress={handleAddBehaviorGroup}
                    >
                      <HStack className="items-center gap-1.5">
                        <Plus
                          color="rgb(var(--color-typography-400))"
                          size={14}
                        />
                        <Text className="text-xs font-medium text-typography-400">
                          {i18n.t("audienceBuilder.addBehaviorGroup")}
                        </Text>
                      </HStack>
                    </Pressable>
                  )}
                </VStack>
              </VStack>
            </VStack>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default DemographicsEditor;
